import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  Accessibility,
  AlertTriangle,
  Armchair,
  Ban,
  Bed,
  Check,
  CircleDot,
  ClipboardPaste,
  Contact,
  Copy,
  Download,
  Edit3,
  Eraser,
  Eye,
  EyeOff,
  FileJson,
  FolderOpen,
  Hand,
  HelpingHand,
  Image as ImageIcon,
  Info,
  Layers,
  Microscope,
  Minus,
  MousePointer2,
  PencilLine,
  Redo2,
  Route,
  Ruler,
  Save,
  Scaling,
  ScanLine,
  Search,
  Shapes,
  Shield,
  ShieldAlert,
  Spline,
  SquareDashed,
  Stethoscope,
  Trash2,
  Truck,
  Type,
  Undo2,
  User,
  Zap
} from 'lucide-react';
import { EditMode, IconType, NodeType, Zone } from '../types';
import { NODE_COLORS } from '../constants';
import { useAppContext } from '../contexts/AppContext';
import { getPathLengthMeters, getZoneRectsAreaM2 } from '../utils/geometry';
import { NODE_LABELS } from '../utils/translations';
import { HUDButton, HUDPanel } from './HUD/HUDPrimitives';

export const ICON_METADATA: Record<IconType, { icon: LucideIcon; label: string; color: string }> = {
  [IconType.DOCTOR]: { icon: Stethoscope, label: 'Gydytojas', color: '#ffffff' },
  [IconType.NURSE]: { icon: User, label: 'Slaugytojas', color: '#f472b6' },
  [IconType.ASSISTANT]: { icon: HelpingHand, label: 'Padėjėjas', color: '#2dd4bf' },
  [IconType.PARAMEDIC]: { icon: Truck, label: 'Paramedikas', color: '#fb923c' },
  [IconType.RECEPTIONIST]: { icon: Contact, label: 'Registratorius', color: '#38bdf8' },
  [IconType.PROTECTION]: { icon: Shield, label: 'Apsauga', color: '#3b82f6' },
  [IconType.BED]: { icon: Bed, label: 'Lova', color: '#38bdf8' },
  [IconType.CHAIR]: { icon: Armchair, label: 'Kėdė', color: '#a78bfa' },
  [IconType.WHEELCHAIR]: { icon: Accessibility, label: 'Vežimėlis', color: '#fb923c' },
  [IconType.EQUIPMENT]: { icon: Microscope, label: 'Įranga', color: '#06b6d4' },
  [IconType.BLOCK]: { icon: Ban, label: 'Blokavimas', color: '#ef4444' },
  [IconType.WARNING]: { icon: AlertTriangle, label: 'Atsargiai', color: '#f59e0b' },
  [IconType.BIOHAZARD]: { icon: ShieldAlert, label: 'Biohazard', color: '#84cc16' },
  [IconType.INFO]: { icon: Info, label: 'Informacija', color: '#3b82f6' }
};

type ToolGroup = 'navigation' | 'create' | 'inspect' | 'destructive';
type ToolTone = 'cyan' | 'emerald' | 'red' | 'slate';

interface ArchitectToolDefinition {
  mode: EditMode;
  label: string;
  icon: LucideIcon;
  group: ToolGroup;
  shortcut: string;
  tone: ToolTone;
}

const ARCHITECT_TOOLS: ArchitectToolDefinition[] = [
  { mode: 'SELECT', label: 'Pasirinkti', icon: MousePointer2, group: 'navigation', shortcut: '', tone: 'cyan' },
  { mode: 'PAN', label: 'Slinkti', icon: Hand, group: 'navigation', shortcut: 'H', tone: 'cyan' },
  { mode: 'ADD_NODE', label: 'Patalpa', icon: CircleDot, group: 'create', shortcut: '', tone: 'cyan' },
  { mode: 'ADD_ZONE', label: 'Zona', icon: SquareDashed, group: 'create', shortcut: '', tone: 'cyan' },
  { mode: 'DRAW_WALL', label: 'Siena', icon: Minus, group: 'create', shortcut: '', tone: 'cyan' },
  { mode: 'ADD_ICON', label: 'Ikona', icon: Shapes, group: 'create', shortcut: '', tone: 'cyan' },
  { mode: 'DRAW_ARCH_PATH', label: 'Maršrutas', icon: Route, group: 'create', shortcut: '', tone: 'emerald' },
  { mode: 'EDIT_PATH', label: 'Taisyti kelią', icon: Spline, group: 'create', shortcut: '', tone: 'emerald' },
  { mode: 'MEASURE_PATH', label: 'Matuoti', icon: Ruler, group: 'inspect', shortcut: '', tone: 'cyan' },
  { mode: 'CALIBRATE_SCALE', label: 'Mastelis', icon: Scaling, group: 'inspect', shortcut: '', tone: 'cyan' },
  { mode: 'DELETE', label: 'Trinti', icon: Eraser, group: 'destructive', shortcut: '', tone: 'red' },
  { mode: 'REGION_DELETE', label: 'Trinti sritį', icon: ScanLine, group: 'destructive', shortcut: '', tone: 'red' }
];

const TOOL_GROUPS: Array<{ id: ToolGroup; label: string }> = [
  { id: 'navigation', label: 'Navigacija' },
  { id: 'create', label: 'Kūrimas' },
  { id: 'inspect', label: 'Tikrinimas' },
  { id: 'destructive', label: 'Trynimas' }
];

const ROOM_CATEGORIES: Array<{ label: string; types: NodeType[] }> = [
  {
    label: 'Srautai',
    types: [NodeType.ENTRANCE, NodeType.AMBULANCE_ENTRANCE, NodeType.RECEPTION, NodeType.TRIAGE, NodeType.WAITING, NodeType.HALLWAY, NodeType.ELEVATOR, NodeType.DOOR, NodeType.CLOAKROOM]
  },
  {
    label: 'Klinikinės patalpos',
    types: [NodeType.EXAM_ROOM, NodeType.PROCEDURE_ROOM, NodeType.ISOLATION_ROOM, NodeType.TRAUMA_BAY, NodeType.WARD, NodeType.BED, NodeType.ARMCHAIRS, NodeType.VIP_ZONE, NodeType.WASHING]
  },
  {
    label: 'Diagnostika',
    types: [NodeType.RADIOLOGY, NodeType.LABORATORY, NodeType.PHARMACY, NodeType.TELEMEDICINE]
  },
  {
    label: 'Personalas ir aptarnavimas',
    types: [NodeType.UTILITY_ROOM, NodeType.STAFF_STATION, NodeType.STAFF_REST, NodeType.SECURITY_POST, NodeType.CONFERENCE_HALL, NodeType.SOCIAL_ROOM, NodeType.WC]
  },
  {
    label: 'Administracija',
    types: [NodeType.CALL_CENTER, NodeType.STATISTICS, NodeType.ADMIN, NodeType.CUSTOM]
  }
];

const inputClass = 'mt-1 min-h-10 w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-xs text-white outline-none transition-colors focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500';
const labelClass = 'block text-xs font-medium text-slate-400';
const iconActionClass = 'rounded p-1.5 text-slate-400 transition-colors hover:bg-slate-800 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400';

interface BuildPanelProps {
  onUploadImage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSave: () => void;
  onLoad: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onExport: () => void;
  onCopy: () => void;
  onPaste: () => void;
  onDuplicate: () => void;
}

export const BuildPanel: React.FC<BuildPanelProps> = ({ onUploadImage, onSave, onLoad, onExport, onCopy, onPaste, onDuplicate }) => {
  const { state, dispatch } = useAppContext();
  const { editMode, selectedNodeType, selectedIconType, selectedItems, nodes, zones, paths, icons, pixelsPerMeter, floorPlanImage } = state;
  const loadInputRef = useRef<HTMLInputElement>(null);
  const bgInputRef = useRef<HTMLInputElement>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<NodeType>>(new Set());
  const [catalogQuery, setCatalogQuery] = useState('');
  const [iconQuery, setIconQuery] = useState('');

  const selectedNodeId = selectedItems.length === 1 && selectedItems[0].kind === 'NODE' ? selectedItems[0].id : null;
  const selectedZoneId = selectedItems.length === 1 && selectedItems[0].kind === 'ZONE' ? selectedItems[0].id : null;
  const selectedIconId = selectedItems.length === 1 && selectedItems[0].kind === 'ICON' ? selectedItems[0].id : null;
  const selectedPathId = selectedItems.length === 1 && selectedItems[0].kind === 'PATH' ? selectedItems[0].id : null;
  const selectedNode = nodes.find(node => node.id === selectedNodeId);
  const selectedIcon = icons.find(icon => icon.id === selectedIconId);

  const groupedZones = useMemo(() => {
    const groups: Record<string, Zone[]> = {};
    zones.forEach(zone => {
      if (!groups[zone.type]) groups[zone.type] = [];
      groups[zone.type].push(zone);
    });
    return groups;
  }, [zones]);

  const normalizedCatalogQuery = catalogQuery.trim().toLocaleLowerCase('lt');
  const filteredRoomCategories = useMemo(() => ROOM_CATEGORIES
    .map(category => ({
      ...category,
      types: category.types.filter(type => NODE_LABELS[type].toLocaleLowerCase('lt').includes(normalizedCatalogQuery))
    }))
    .filter(category => category.types.length > 0), [normalizedCatalogQuery]);

  const filteredIconTypes = useMemo(() => (Object.keys(ICON_METADATA) as IconType[])
    .filter(type => ICON_METADATA[type].label.toLocaleLowerCase('lt').includes(iconQuery.trim().toLocaleLowerCase('lt'))), [iconQuery]);

  useEffect(() => {
    if (!selectedZoneId) return;
    const selectedZone = zones.find(zone => zone.id === selectedZoneId);
    if (selectedZone && !expandedGroups.has(selectedZone.type)) {
      setExpandedGroups(previous => new Set(previous).add(selectedZone.type));
    }
  }, [selectedZoneId, zones, expandedGroups]);

  const startPathDrawing = (mode: 'DRAW_PATH' | 'DRAW_ARCH_PATH') => {
    dispatch({ type: 'SET_EDIT_MODE', payload: mode });
    dispatch({ type: 'SET_SELECTION', payload: [] });
  };

  const activateTool = (tool: ArchitectToolDefinition) => {
    if (tool.mode === 'DRAW_ARCH_PATH') {
      startPathDrawing('DRAW_ARCH_PATH');
      return;
    }
    dispatch({ type: 'SET_EDIT_MODE', payload: tool.mode });
  };

  const toggleGroup = (type: NodeType) => {
    setExpandedGroups(previous => {
      const next = new Set(previous);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  const calculateZoneArea = (zone: Zone) => getZoneRectsAreaM2(zone.rects || [], pixelsPerMeter);
  const isAddingNode = editMode === 'ADD_NODE';
  const isAddingZone = editMode === 'ADD_ZONE';
  const isAddingIcon = editMode === 'ADD_ICON';
  const showCatalog = isAddingNode || isAddingZone;

  return (
    <div className="space-y-4">
      <HUDPanel title="Pagrindiniai įrankiai" icon={<MousePointer2 size={14} />}>
        <div className="space-y-3">
          {TOOL_GROUPS.map(group => (
            <div key={group.id} className="space-y-1.5">
              <div className="text-xs font-medium text-slate-400">{group.label}</div>
              <div className="grid grid-cols-2 gap-1.5">
                {ARCHITECT_TOOLS.filter(tool => tool.group === group.id).map(tool => {
                  const ToolIcon = tool.icon;
                  return (
                    <HUDButton
                      key={tool.mode}
                      active={editMode === tool.mode}
                      title={`${tool.label}${tool.shortcut ? ` (${tool.shortcut})` : ''}`}
                      variant={tool.tone}
                      label={tool.label}
                      shortcut={tool.shortcut}
                      className="justify-start"
                      icon={<ToolIcon size={16} />}
                      onClick={() => activateTool(tool)}
                    />
                  );
                })}
              </div>
            </div>
          ))}
          <div className="space-y-1.5 border-t border-slate-800 pt-2.5">
            <div className="text-xs font-medium text-slate-400">Istorija</div>
            <div className="grid grid-cols-2 gap-1.5">
              <HUDButton disabled={state.history.length === 0} title="Atšaukti (Ctrl/Cmd+Z)" label="Atšaukti" shortcut="Ctrl Z" className="justify-start" icon={<Undo2 size={16} />} onClick={() => dispatch({ type: 'UNDO' })} />
              <HUDButton disabled={state.future.length === 0} title="Grąžinti (Ctrl/Cmd+Y)" label="Grąžinti" shortcut="Ctrl Y" className="justify-start" icon={<Redo2 size={16} />} onClick={() => dispatch({ type: 'REDO' })} />
            </div>
          </div>
        </div>
      </HUDPanel>

      {selectedItems.length > 0 && (
        <HUDPanel title="Objekto savybės" icon={<Edit3 size={14} />} color="cyan">
          <div className="space-y-3">
            <div>
              <div className="text-xs font-semibold text-cyan-200">
                {selectedItems.length === 1 ? 'Pasirinktas 1 objektas' : `Pasirinkta objektų: ${selectedItems.length}`}
              </div>
              <p className="mt-1 text-xs leading-5 text-slate-400">Tempkite pažymėtus objektus arba naudokite kopijavimo veiksmus.</p>
            </div>

            {(selectedNode || selectedIcon) && (
              <div className="space-y-3 border-t border-slate-800 pt-3">
                <label className={labelClass}>
                  Pavadinimas
                  <input
                    className={inputClass}
                    value={(selectedNode?.label ?? selectedIcon?.label) || ''}
                    onChange={event => selectedNode
                      ? dispatch({ type: 'UPDATE_NODE', payload: { id: selectedNode.id, updates: { label: event.target.value } } })
                      : dispatch({ type: 'UPDATE_ICON', payload: { id: selectedIcon!.id, updates: { label: event.target.value } } })}
                  />
                </label>
                {selectedNode && (
                  <div className="grid grid-cols-2 gap-2">
                    {([
                      ['Talpa', 'capacity', selectedNode.capacity],
                      ['Apdorojimas', 'processingTime', selectedNode.processingTime],
                      ['Greičio koef.', 'speedModifier', selectedNode.speedModifier]
                    ] as const).map(([label, key, value]) => (
                      <label key={key} className={labelClass}>
                        {label}
                        <input
                          type="number"
                          min="0"
                          step={key === 'speedModifier' ? '0.1' : '1'}
                          value={value}
                          className={inputClass}
                          onChange={event => dispatch({ type: 'UPDATE_NODE', payload: { id: selectedNode.id, updates: { [key]: Math.max(0, Number(event.target.value)) } } })}
                        />
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-3 gap-2 border-t border-slate-800 pt-3">
              <HUDButton icon={<Copy size={14} />} onClick={onCopy}>Kopijuoti</HUDButton>
              <HUDButton icon={<ClipboardPaste size={14} />} onClick={onPaste}>Įklijuoti</HUDButton>
              <HUDButton icon={<Copy size={14} />} onClick={onDuplicate}>Dubliuoti</HUDButton>
            </div>
            <HUDButton className="w-full" variant="red" icon={<Trash2 size={14} />} onClick={() => dispatch({ type: 'DELETE_SELECTION', payload: selectedItems })}>
              {selectedItems.length === 1 ? 'Pašalinti objektą' : 'Pašalinti objektus'}
            </HUDButton>
          </div>
        </HUDPanel>
      )}

      {showCatalog && (
        <HUDPanel
          key={isAddingNode ? 'room-catalog' : 'zone-catalog'}
          title={isAddingNode ? 'Patalpų katalogas' : 'Zonų katalogas'}
          icon={isAddingNode ? <CircleDot size={14} /> : <SquareDashed size={14} />}
          color={isAddingZone ? 'emerald' : 'cyan'}
          collapsible
        >
          <div className="space-y-3">
            <label className="relative block">
              <Search size={15} className="pointer-events-none absolute left-3 top-3 text-slate-500" />
              <input
                type="search"
                value={catalogQuery}
                onChange={event => setCatalogQuery(event.target.value)}
                placeholder="Ieškoti patalpos ar zonos..."
                className="min-h-10 w-full rounded-md border border-slate-700 bg-slate-900 py-2 pl-9 pr-3 text-xs text-white outline-none placeholder:text-slate-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
              />
            </label>
            <div className="max-h-[340px] space-y-4 overflow-y-auto pr-1 custom-scrollbar">
              {filteredRoomCategories.map(category => (
                <div key={category.label} className="space-y-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400">{category.label}</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {category.types.map(type => {
                      const isSelected = selectedNodeType === type;
                      return (
                        <button
                          key={type}
                          type="button"
                          aria-pressed={isSelected}
                          onClick={() => dispatch({ type: 'SET_SELECTED_NODE_TYPE', payload: type })}
                          className={`flex min-h-9 items-center gap-2 rounded-md border px-2 py-1.5 text-left text-xs font-medium leading-4 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 ${isSelected ? 'border-cyan-500 bg-cyan-950 text-cyan-100' : 'border-slate-700 bg-slate-900/50 text-slate-300 hover:border-slate-500 hover:bg-slate-800'}`}
                        >
                          <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: NODE_COLORS[type] }} />
                          <span className="min-w-0 flex-1 break-words">{NODE_LABELS[type]}</span>
                          {isSelected && <Check size={14} className="shrink-0 text-cyan-300" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              {filteredRoomCategories.length === 0 && <p className="py-5 text-center text-xs text-slate-400">Atitikmenų nerasta.</p>}
            </div>
          </div>
        </HUDPanel>
      )}

      {isAddingIcon && (
        <HUDPanel title="Ikonų katalogas" icon={<Shapes size={14} />} color="cyan" collapsible>
          <div className="space-y-3">
            <label className="relative block">
              <Search size={15} className="pointer-events-none absolute left-3 top-3 text-slate-500" />
              <input
                type="search"
                value={iconQuery}
                onChange={event => setIconQuery(event.target.value)}
                placeholder="Ieškoti ikonos..."
                className="min-h-10 w-full rounded-md border border-slate-700 bg-slate-900 py-2 pl-9 pr-3 text-xs text-white outline-none placeholder:text-slate-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
              />
            </label>
            <div className="grid max-h-[280px] grid-cols-2 gap-2 overflow-y-auto pr-1 custom-scrollbar">
              {filteredIconTypes.map(type => {
                const meta = ICON_METADATA[type];
                const isSelected = selectedIconType === type;
                return (
                  <button
                    key={type}
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => dispatch({ type: 'SET_SELECTED_ICON_TYPE', payload: type })}
                    className={`flex min-h-9 items-center gap-2 rounded-md border px-2 py-1.5 text-left text-xs font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 ${isSelected ? 'border-cyan-500 bg-cyan-950 text-cyan-100' : 'border-slate-700 bg-slate-900/50 text-slate-300 hover:border-slate-500 hover:bg-slate-800'}`}
                  >
                    <meta.icon size={16} className="shrink-0" style={{ color: meta.color }} />
                    <span className="min-w-0 flex-1 break-words">{meta.label}</span>
                    {isSelected && <Check size={14} className="shrink-0 text-cyan-300" />}
                  </button>
                );
              })}
            </div>
          </div>
        </HUDPanel>
      )}

      <HUDPanel title="Brėžinys" icon={<ImageIcon size={14} />} collapsible defaultCollapsed>
        <div className="space-y-3">
          <HUDButton className="w-full" icon={<ImageIcon size={16} />} onClick={() => bgInputRef.current?.click()}>
            {floorPlanImage ? 'Keisti brėžinį' : 'Įkelti brėžinį'}
          </HUDButton>
          <input type="file" ref={bgInputRef} className="hidden" accept="image/*" onChange={onUploadImage} />
          {floorPlanImage && (
            <>
              {([
                ['Permatomumas', 'opacity', state.mapOpacity, 0, 1, 0.05],
                ['Šviesumas', 'brightness', state.mapBrightness, 0.2, 2, 0.05],
                ['Kontrastas', 'contrast', state.mapContrast, 0.2, 2, 0.05]
              ] as const).map(([label, key, value, min, max, step]) => (
                <label key={key} className={labelClass}>
                  {label}
                  <input
                    className="mt-2 w-full accent-cyan-500"
                    type="range" min={min} max={max} step={step} value={value}
                    onChange={event => dispatch({ type: 'SET_MAP_ADJUSTMENTS', payload: { [key]: Number(event.target.value) } })}
                  />
                </label>
              ))}
              <HUDButton className="w-full" variant="red" icon={<Trash2 size={16} />} onClick={() => dispatch({ type: 'SET_FLOOR_PLAN', payload: null })}>Pašalinti foną</HUDButton>
            </>
          )}
        </div>
      </HUDPanel>

      <HUDPanel title="Maršrutai ir ryšiai" icon={<Route size={14} />} color="emerald" collapsible defaultCollapsed={paths.length === 0}>
        <div className="mb-3 grid grid-cols-2 gap-2">
          <HUDButton active={editMode === 'DRAW_PATH'} variant="emerald" icon={<Zap size={16} />} onClick={() => startPathDrawing('DRAW_PATH')}>Sim. ryšys</HUDButton>
          <HUDButton active={editMode === 'EDIT_PATH'} variant="emerald" icon={<Spline size={16} />} onClick={() => dispatch({ type: 'SET_EDIT_MODE', payload: 'EDIT_PATH' })}>Taisyti kelią</HUDButton>
        </div>
        <div className="max-h-[220px] space-y-2 overflow-y-auto pr-1 custom-scrollbar">
          {paths.map(path => {
            const distance = getPathLengthMeters(path.points, pixelsPerMeter);
            const isArchitectural = path.kind ? path.kind === 'ARCHITECTURAL' : !path.name.includes('Sim.');
            return (
              <div key={path.id} className={`rounded-md border p-2.5 ${selectedPathId === path.id ? 'border-emerald-600 bg-emerald-950/40' : 'border-slate-800 bg-slate-900/50'}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <span className="h-4 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: path.color }} />
                    <span className="truncate text-xs font-semibold text-slate-200">{path.name}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button aria-label={path.visible ? 'Slėpti maršrutą' : 'Rodyti maršrutą'} title={path.visible ? 'Slėpti' : 'Rodyti'} onClick={() => dispatch({ type: 'UPDATE_PATH', payload: { id: path.id, updates: { visible: !path.visible } } })} className={iconActionClass}>{path.visible ? <Eye size={14} /> : <EyeOff size={14} />}</button>
                    <button aria-label="Redaguoti maršrutą" title="Redaguoti" onClick={() => {
                      dispatch({ type: 'SET_EDIT_MODE', payload: 'EDIT_PATH' });
                      dispatch({ type: 'SET_SELECTION', payload: [{ kind: 'PATH', id: path.id }] });
                    }} className={iconActionClass}><Edit3 size={14} /></button>
                    <button aria-label="Pašalinti maršrutą" title="Pašalinti" onClick={() => dispatch({ type: 'DELETE_PATH', payload: path.id })} className={`${iconActionClass} hover:text-red-300`}><Trash2 size={14} /></button>
                  </div>
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-xs text-slate-400">{isArchitectural ? 'Planavimas' : 'Simuliacija'}</span>
                  <span className="font-mono text-xs text-emerald-300">{distance.toFixed(1)} m</span>
                </div>
              </div>
            );
          })}
          {paths.length === 0 && <p className="py-3 text-center text-xs text-slate-400">Maršrutų dar nėra.</p>}
        </div>
      </HUDPanel>

      <HUDPanel title="Zonų valdymas" icon={<SquareDashed size={14} />} color="cyan" collapsible defaultCollapsed={zones.length === 0}>
        <div className="mb-4 max-h-[320px] space-y-2 overflow-y-auto pr-1 custom-scrollbar">
          {Object.entries(groupedZones).sort().map(([type, groupZones]) => {
            const nodeType = type as NodeType;
            const isExpanded = expandedGroups.has(nodeType);
            const groupTotalArea = groupZones.reduce((total, zone) => total + calculateZoneArea(zone), 0);
            return (
              <div key={type}>
                <button type="button" aria-expanded={isExpanded} onClick={() => toggleGroup(nodeType)} className={`flex min-h-9 w-full items-center justify-between gap-2 rounded-md border px-2 py-1.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 ${isExpanded ? 'border-cyan-800 bg-cyan-950/40' : 'border-slate-800 bg-slate-900/50 hover:border-slate-600'}`}>
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="h-4 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: NODE_COLORS[nodeType] }} />
                    <span className="truncate text-xs font-semibold text-slate-200">{NODE_LABELS[nodeType]}</span>
                  </span>
                  <span className="shrink-0 font-mono text-xs text-slate-400">{groupTotalArea.toFixed(1)} m²</span>
                </button>
                {isExpanded && (
                  <div className="ml-3 mt-1 space-y-1 border-l border-slate-800 pl-2">
                    {[...groupZones].reverse().map(zone => (
                      <div key={zone.id} className={`rounded-md border p-2 ${selectedZoneId === zone.id ? 'border-cyan-700 bg-cyan-950/30' : 'border-slate-800 bg-slate-900/30'}`}>
                        <div className="flex items-center justify-between gap-2">
                          <button onClick={() => dispatch({ type: 'SET_SELECTION', payload: [{ kind: 'ZONE', id: zone.id }] })} className="flex min-h-9 min-w-0 flex-1 items-center gap-2 rounded px-1 text-left text-xs text-slate-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400">
                            <PencilLine size={14} className="shrink-0 text-cyan-400" />
                            <span className="truncate">{zone.label || `Zona ${zone.id.slice(0, 4).toUpperCase()}`}</span>
                          </button>
                          <button aria-label="Pašalinti zoną" title="Pašalinti" onClick={() => dispatch({ type: 'DELETE_ZONE', payload: zone.id })} className={`${iconActionClass} hover:text-red-300`}><Trash2 size={14} /></button>
                        </div>
                        {selectedZoneId === zone.id && (
                          <label className="mt-2 flex items-center gap-2 rounded-md border border-slate-700 bg-slate-950 p-2">
                            <Type size={14} className="shrink-0 text-cyan-400" />
                            <span className="sr-only">Zonos pavadinimas</span>
                            <input type="text" placeholder="Pervadinti..." className="min-w-0 flex-1 bg-transparent text-xs text-white outline-none placeholder:text-slate-500" value={zone.label || ''} onChange={event => dispatch({ type: 'UPDATE_ZONE', payload: { id: zone.id, updates: { label: event.target.value } } })} />
                          </label>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {zones.length === 0 && <p className="py-3 text-center text-xs text-slate-400">Zonų dar nėra.</p>}
        </div>
        <HUDButton variant="emerald" className="w-full" icon={<Layers size={16} />} onClick={() => dispatch({ type: 'MERGE_ZONES' })}>Automatinis suliejimas</HUDButton>
      </HUDPanel>

      <HUDPanel title="Duomenų valdymas" icon={<Save size={14} />} collapsible defaultCollapsed>
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <HUDButton className="flex-1" icon={<FileJson size={16} />} onClick={onSave}>Išsaugoti</HUDButton>
            <HUDButton className="flex-1" icon={<FolderOpen size={16} />} onClick={() => loadInputRef.current?.click()}>Įkelti</HUDButton>
          </div>
          <HUDButton className="w-full" variant="slate" icon={<Download size={16} />} onClick={onExport}>Eksportuoti PNG</HUDButton>
          <input type="file" ref={loadInputRef} className="hidden" accept=".json" onChange={onLoad} />
        </div>
      </HUDPanel>
    </div>
  );
};

export default BuildPanel;
